import { type ReactNode } from 'react';
import { X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// A bottom-sheet single/multi selection list, matching the design's picker modals.

interface PickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  footer?: ReactNode;
}

export function PickerSheet({ isOpen, onClose, title, children, footer }: PickerSheetProps) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card border border-border rounded-3xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-secondary">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">{children}</div>
        {footer && <div className="p-4 border-t border-border">{footer}</div>}
      </div>
    </div>
  );
}

export function PickerRow({
  label,
  selected,
  onClick,
  sublabel,
}: {
  label: ReactNode;
  selected: boolean;
  onClick: () => void;
  sublabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-left',
        selected ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary text-foreground',
      )}
    >
      <div>
        <span className="text-sm font-medium">{label}</span>
        {sublabel && (
          <p className={cn('text-xs', selected ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
            {sublabel}
          </p>
        )}
      </div>
      {selected && <Check className="w-4 h-4 flex-shrink-0" />}
    </button>
  );
}
